import type { V1Rules, V1Skill, V1Stats, V1Trait } from "./types";

export interface V1RuntimeBattler extends V1Stats {
  hit: number;
  eva: number;
  cri: number;
  cev: number;
  mev: number;
  pdr: number;
  mdr: number;
}

export interface V1SkillOutcome {
  landed: boolean;
  critical: boolean;
  damage: number;
}

const traitSum = (traits: readonly V1Trait[], code: number, dataId: number) =>
  traits
    .filter((trait) => trait.code === code && trait.dataId === dataId)
    .reduce((sum, trait) => sum + trait.value, 0);

const traitProduct = (traits: readonly V1Trait[], code: number, dataId: number) =>
  traits
    .filter((trait) => trait.code === code && trait.dataId === dataId)
    .reduce((product, trait) => product * trait.value, 1);

const STAT_KEYS = [
  "maxHp", "maxMp", "attack", "defense",
  "magicAttack", "magicDefense", "agility", "luck",
] as const;

export const v1ApplyParameterRates = (
  stats: V1Stats,
  traits: readonly V1Trait[],
): V1Stats => {
  const result = { ...stats };
  STAT_KEYS.forEach((key, dataId) => {
    result[key] = Math.round(result[key] * traitProduct(traits, 21, dataId));
  });
  result.maxHp = Math.max(1, result.maxHp);
  return result;
};

export const v1RuntimeBattler = (
  stats: V1Stats,
  traits: readonly V1Trait[],
): V1RuntimeBattler => ({
  ...stats,
  hit: traitSum(traits, 22, 0),
  eva: traitSum(traits, 22, 1),
  cri: traitSum(traits, 22, 2),
  cev: traitSum(traits, 22, 3),
  mev: traitSum(traits, 22, 4),
  pdr: traitProduct(traits, 23, 6),
  mdr: traitProduct(traits, 23, 7),
});

const randomInt = (maximumExclusive: number, random: () => number) =>
  Math.floor(maximumExclusive * random());

const applyVariance = (damage: number, variancePercent: number, random: () => number) => {
  const amplitude = Math.floor(Math.max(Math.abs(damage) * variancePercent / 100, 0));
  const variation = randomInt(amplitude + 1, random) + randomInt(amplitude + 1, random) - amplitude;
  return damage >= 0 ? damage + variation : damage - variation;
};

export const v1SkillBaseDamage = (
  skill: V1Skill,
  levelIndex: number,
  user: V1RuntimeBattler,
  target: V1RuntimeBattler,
): number => {
  const power = skill.powers[Math.max(0, Math.min(skill.powers.length - 1, levelIndex))] ?? 0;
  const extra = skill.extras[Math.max(0, Math.min(skill.extras.length - 1, levelIndex))] ?? 0;
  if (skill.formulaCode === 0) return 0;
  if (skill.formulaCode === 1) {
    return Math.max(user.attack * power - target.defense, 0)
      + user.magicAttack - target.magicDefense + extra;
  }
  if (skill.formulaCode === 2) {
    return user.attack * power - target.defense
      + user.magicAttack - target.magicDefense + extra;
  }
  throw new Error(`技能 ${skill.id} 使用了未支持的安全公式 ${skill.formulaCode}`);
};

export const rollV1SkillOutcome = (
  skill: V1Skill,
  levelIndex: number,
  user: V1RuntimeBattler,
  target: V1RuntimeBattler,
  rules: V1Rules,
  random: () => number,
): V1SkillOutcome => {
  const skillHitRate = skill.successRatePercent / 100;
  const hitRate = skill.hitTypeId === 1
    ? skillHitRate * (user.hit - target.eva)
    : skill.hitTypeId === 2
      ? skillHitRate * (1 - target.mev)
      : skillHitRate;
  if (random() >= hitRate) return { landed: false, critical: false, damage: 0 };
  random(); // APK 的零闪避公式仍会消耗一次闪避随机数。

  const baseDamage = Math.max(0, v1SkillBaseDamage(skill, levelIndex, user, target));
  if (skill.damageTypeId === 0) return { landed: true, critical: false, damage: 0 };

  const criticalRate = !skill.critical
    ? 0
    : skill.criticalRatePercent === null
      ? user.cri - target.cev
      : skill.criticalRatePercent / 100;
  const critical = random() < criticalRate;
  let damage = baseDamage;
  if (critical) damage *= rules.criticalDamagePercent / 100 + user.mev;
  if (skill.hitTypeId === 1) damage *= target.pdr;
  if (skill.hitTypeId === 2) damage *= target.mdr;
  if (critical) damage += rules.criticalFlatBonus;
  damage = applyVariance(damage, skill.variancePercent, random);
  if (baseDamage > 0) damage = Math.max(rules.minimumDamage, damage);
  damage = Math.min(rules.maximumDamage, Math.max(0, Math.round(damage)));
  return { landed: true, critical, damage };
};
